import React, { FC, useEffect, useState } from 'react';
import { toAbsoluteUrl } from '../../utils';
import { RelatedMerchants } from './RelatedMerchants';
import { Title } from '../developers/page-components/Title';
import { ProviderActiveToggle } from './ProviderActiveToggle';
import { IFormattedProviderProps } from '../../store/tables/admin/provider-data';
import { IModalWindow, ModalWindow } from '../modal/ModalWindow';
import { ProviderDetailsNotification } from './ProviderDetailsNotification';
import { setProviders } from '../../store/payments/actions/providers';
import { ProviderActiveModal } from './ProviderActiveModal';
import { usePaymentContext } from '../../store/payments/payments-context';
import { IReleaseState } from '../../routing/routing-helper/provider-navigation';

import './ProviderDetailsHeader.scss';
import { ProviderDetailsConfigurations } from './ProviderDetailsConfigurations';

export interface IProviderActiveParams {
  isActive: boolean;
  id: string;
}

interface Props {
  data: IFormattedProviderProps,
  setReleaseState: (state: IReleaseState) => void;
}
export const ProviderDetailsHeader: FC<Props> = ({ data, setReleaseState }) => {

  const { dispatch } = usePaymentContext();

  const [localIsActive, setLocalIsActive] = useState<boolean>(data?.isActive);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string[]>(null);
  const [modalState, setModalState] = useState<IModalWindow>({
    isOpen: false
  });

  const closeModal = ():void => setModalState(prevState => ({ ...prevState, isOpen: false }));

  useEffect(() => {
    if (error) {
      openDeactivateModal();
    }
  }, [error]);

  const handleDeactivateProvider = async (): Promise<void> => {
    try {
      const params: IProviderActiveParams = {
        isActive: false,
        id: data?.id
      };

      await setProviders({ dispatch, params, setError });
      setLocalIsActive(false);
      closeModal();
    } catch (e) {
      closeModal();
    }
  };

  const openDeactivateModal = (): void => {

    setModalState({
      isOpen: true,
      modalTitle: 'Deactivating provider',
      content: <ProviderActiveModal error={error} />,
      contentClassName: 'd-block',
      backdrop: 'static',
      minHeight: 150,
      onClose: () => {
        closeModal();
        setError(null);
      },
      onCancel: () => {
        closeModal();
        setError(null);
      },
      onConfirm: handleDeactivateProvider,
      isConfirmDisabled: error?.length > 0,
      additionalConfirmButtonClass: 'w-120px',
      confirmButtonText: 'Deactivate',
    });
  };

  const handleToggleChange = async (): Promise<void> => {

    if (localIsActive) {
      openDeactivateModal();
    } else {
      await activateProvider();
    }
  };

  const activateProvider = async (): Promise<void> => {
    try {
      setLoading(true);
      setLocalIsActive(true);

      const params: IProviderActiveParams = {
        isActive: true,
        id: data?.id
      };

      // await setPaymentGateway({ dispatch, params });
      await setProviders({ dispatch, params });

      setLoading(false);
    } catch (e) {
      console.log('activateProvider', e.responce);
      setLocalIsActive(false);
      setLoading(false);
    }
  };

  return (
    <>
      <div className="d-flex align-items-center justify-content-between mb-5">
        <div className="provider-details-logo-wrap">
          <img
            src={data?.providerLogo
              ? data?.providerLogo
              : toAbsoluteUrl('/media/flex-logo-small.svg')
            }
            alt="icon"
            className="provider-details-logo"
          />
        </div>
        <div className="d-flex align-items-center">
          <RelatedMerchants
            name={`Related Merchants (${data?.relatedMerchantsCount})`}
            secondaryName='No Related Merchants'
            additionalClass='fs-6 me-10 no-letter-spacing mt-5'
            secondaryAdditionalClass='fs-6 me-10 text-muted mt-5'
            relatedMerchantsList={data?.relatedMerchants}
            relatedMerchantsCount={data?.relatedMerchantsCount}
          />

          <ProviderDetailsConfigurations configurations={data?.configuration} />

          <div>
            <Title title="Active" additionalClass="fs-7 text-muted" />
            <ProviderActiveToggle
              localIsActive={localIsActive}
              handleInputChange={handleToggleChange}
              isLoading={loading}
            />
          </div>
        </div>

      </div>

      <ProviderDetailsNotification
        data={data}
        isActive={localIsActive}
        openReleaseModal={(): void => setReleaseState({ isOpen: true, providerId: data?.id })}
        onDeactivate={handleToggleChange}
      />

      <ModalWindow
        {...modalState}
      />
    </>
  );
};